import { motion } from 'framer-motion';
import { ExternalLink, TrendingUp, BarChart3, Clock, Target } from 'lucide-react';
import Card from '../ui/Card';

export default function SectionMarche({ data }) {
  if (!data) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <section id="marche" className="section-padding bg-black">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-primary-500 mb-4">{data.title}</h2>
          <p className="text-secondary-200 text-lg">{data.subtitle}</p>
        </motion.div>

        {/* Budgets Professionnels */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="text-primary-500" />
            {data.sections.budgetsPro.title}
          </h3>
          <p className="text-secondary-200 mb-6">{data.sections.budgetsPro.intro}</p>

          <div className="grid gap-4">
            {data.sections.budgetsPro.data.map((item, idx) => (
              <Card key={idx} hover>
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-3">
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-white mb-2">{item.type}</h4>
                    <div className="space-y-1">
                      <p className="text-secondary-200 text-sm">
                        <span className="font-semibold">Budget total :</span> {item.budgetTotal}
                      </p>
                      {item.postProd && (
                        <p className="text-secondary-200 text-sm">
                          <span className="font-semibold">Post-production :</span> {item.postProd}
                        </p>
                      )}
                      {item.coutMinute && (
                        <p className="text-secondary-200 text-sm">
                          <span className="font-semibold">Coût minute :</span> {item.coutMinute}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-primary-500 font-bold text-2xl md:text-3xl">
                      {item.budgetTotal}
                    </span>
                  </div>
                </div>

                {/* Source */}
                <a
                  href={item.source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs text-primary-400 hover:text-primary-300 transition mb-2"
                >
                  <ExternalLink size={14} />
                  Source : {item.source.name}
                </a>

                {item.source.citation && (
                  <blockquote className="pl-4 border-l-2 border-primary-500/30 text-sm text-secondary-400 italic">
                    "{item.source.citation}"
                  </blockquote>
                )}
              </Card>
            ))}
          </div>
        </motion.div>

        {/* Tarifs Freelance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="text-primary-500" />
            {data.sections.tarifsFreelance.title}
          </h3>
          <p className="text-secondary-200 mb-6">{data.sections.tarifsFreelance.intro}</p>

          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-secondary-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-white font-semibold">Profil</th>
                    <th className="px-4 py-3 text-left text-white font-semibold">TJM</th>
                    <th className="px-4 py-3 text-left text-white font-semibold">Sources</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sections.tarifsFreelance.data.map((item, idx) => (
                    <tr
                      key={idx}
                      className={`${
                        idx !== data.sections.tarifsFreelance.data.length - 1
                          ? 'border-b border-secondary-800'
                          : ''
                      }`}
                    >
                      <td className="px-4 py-4 text-white font-medium">{item.profil}</td>
                      <td className="px-4 py-4 text-primary-500 font-bold text-lg">{item.tjm}</td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-1">
                          {item.sources.map((src, i) => (
                            <a
                              key={i}
                              href={src.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary-400 hover:text-primary-300 flex items-center gap-1 transition"
                            >
                              <ExternalLink size={12} />
                              {src.name}
                            </a>
                          ))}
                        </div>
                        {item.sources[0]?.citation && (
                          <p className="text-xs text-secondary-400 italic mt-2">
                            "{item.sources[0].citation}"
                          </p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </motion.div>

        {/* Temps de Réalisation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Clock className="text-primary-500" />
            {data.sections.tempsRealisation.title}
          </h3>

          <div className="grid md:grid-cols-2 gap-4">
            {data.sections.tempsRealisation.data.map((item, idx) => (
              <Card key={idx} variant={item.application ? 'gradient' : 'default'}>
                {item.regle && (
                  <>
                    <p className="text-white font-bold text-lg mb-2">{item.regle}</p>
                    <a
                      href={item.source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-xs text-primary-400 hover:text-primary-300 transition mb-2"
                    >
                      <ExternalLink size={14} />
                      {item.source.name}
                    </a>
                    <blockquote className="pl-4 border-l-2 border-primary-500/30 text-sm text-secondary-400 italic">
                      "{item.source.citation}"
                    </blockquote>
                  </>
                )}
                {item.application && (
                  <>
                    <p className="text-white font-bold mb-2">{item.application}</p>
                    <p className="text-primary-500 text-xl font-bold mb-2">{item.estimation}</p>
                    <p className="text-secondary-300 text-sm">{item.detail}</p>
                  </>
                )}
              </Card>
            ))}
          </div>
        </motion.div>

        {/* Notre Positionnement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <Card variant="gradient" className="bg-gradient-to-r from-primary-500/10 to-transparent border border-primary-500/20">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Target className="text-primary-500" />
              {data.sections.positionnement.title}
            </h3>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {data.sections.positionnement.comparaisons.map((comp, idx) => (
                <div key={idx} className="bg-black/50 rounded-lg p-6">
                  <p className="text-sm text-secondary-300 mb-3">{comp.reference}</p>
                  <div className="flex items-baseline gap-3 mb-3">
                    <span className="text-secondary-500 line-through text-lg">{comp.prix}</span>
                    <span className="text-primary-500 text-3xl font-bold">{comp.notre}</span>
                  </div>
                  <p className="text-green-400 text-sm font-semibold">
                    Économie : {comp.economie}
                  </p>
                </div>
              ))}
            </div>

            <div className="bg-black/30 rounded-lg p-6">
              <p className="text-secondary-200 text-sm leading-relaxed">
                <span className="font-bold text-white">Pourquoi ces tarifs optimisés ? </span>
                {data.sections.positionnement.explication}
              </p>
            </div>
          </Card>
        </motion.div>

        {/* Sources Complètes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <details className="cursor-pointer">
            <summary className="text-primary-400 hover:text-primary-300 transition font-medium text-lg mb-4">
              📚 Voir toutes les sources ({data.sources.length})
            </summary>
            <div className="grid gap-3">
              {data.sources.map((source, idx) => (
                <a
                  key={idx}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Card hover className="group">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="text-white font-medium group-hover:text-primary-400 transition mb-1">
                          {source.title}
                        </h4>
                        <p className="text-xs text-secondary-400 mb-2">{source.description}</p>
                        <div className="flex items-center gap-2 text-xs text-primary-400">
                          <ExternalLink size={12} />
                          <span className="truncate">{source.url}</span>
                        </div>
                      </div>
                      <span className="text-xs text-secondary-500 flex-shrink-0">
                        {source.date}
                      </span>
                    </div>
                  </Card>
                </a>
              ))}
            </div>
          </details>
        </motion.div>

        {/* Métadonnées */}
        <div className="mt-8 text-center text-xs text-secondary-600 space-y-1">
          <p>
            Analyse réalisée le {data.metadata.lastUpdated} par {data.metadata.analysisBy}
          </p>
          <p>{data.metadata.methodology}</p>
        </div>
      </div>
    </section>
  );
}
